import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Clock, FileCheck2, MapPin, Sparkles, Target, XCircle, Building2 } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { Disclaimer } from "@/components/Disclaimer";
import { MiniTasteTest } from "@/components/MiniTasteTest";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";
import { FREE_CTA, PREMIUM_CTA } from "@/lib/cta";
import { FreeQuizOffer } from "@/components/FreeQuizOffer";

const faqs = [
  {
    q: "Är övningsprovet samma sak som kommunens kunskapsprov?",
    a: "Nej. Övningsprovet på Alkoholprovet är en pedagogisk simulering byggd på alkohollagen och Folkhälsomyndighetens kunskapsmoduler. Kommunens skarpa kunskapsprov är sekretessbelagt och får inte återpubliceras. Vårt mål är att du ska känna igen frågetyperna, vänja dig vid tidspressen och ha läst in rätt områden – inte att memorera exakta frågor.",
  },
  {
    q: "Hur många frågor innehåller ett övningsprov?",
    a: "Vår provsimulering har två lägen: modulträning med 15 frågor (10 minuter) för fokuserad träning på ett av de 16 områdena, och fullt prov med 60 frågor (30 minuter) som speglar det stora kommunala provet. Båda lägena drar från samma frågedatabas på 800 frågor.",
  },
  {
    q: "Hur lång tid har jag på mig?",
    a: "Det riktiga kunskapsprovet hos kommunen ger normalt 90 minuter för upp till 60 frågor (≈90 sekunder/fråga). Vår fullängdssimulering kör 60 frågor på 30 minuter (≈30 sekunder/fråga) för att medvetet träna upp tempot – klarar du tempot här klarar du tempot där. Modulträning är 15 frågor på 10 minuter.",
  },
  {
    q: "Måste jag ha konto för att göra övningsprovet?",
    a: "Nej. Du kan testa fem exempelfrågor direkt på den här sidan utan konto. För att göra en fullständig provsimulering eller spara dina resultat behöver du ett gratis konto. Premium krävs först när du vill ha obegränsade frågor och statistik.",
  },
  {
    q: "Vad krävs för att bli godkänd?",
    a: "Enligt Folkhälsomyndighetens föreskrifter FoHMFS 2014:7 krävs minst 75 % rätt totalt. Många kommuner kräver dessutom godkänt per delområde, så jämn kunskap över alla 16 områden är viktigt. Ligger du stadigt över 80 % i vår provsimulering är du normalt redo för det skarpa provet.",
  },
  {
    q: "Vilka områden täcks?",
    a: "Alla 15 obligatoriska kunskapsområden: alkoholpolitik, tillsyn, servering, mat, marknadsföring, ordning och nykterhet, åldersgränser, serveringsansvarig, lagad mat, bokföring, kassaregister, brand, narkotika, lokalkrav och de nya reglerna 2026.",
  },
  {
    q: "Är frågorna uppdaterade för 2026 års regler?",
    a: "Ja. När matkravet och kravet på eget kök ändrades 1 juni 2026 uppdaterade vi alla frågor inom områdena ”lokalkrav” och ”lagad mat”. Du tränar alltid på den lagtext som gäller nu – inte den som gällde innan.",
  },
  {
    q: "Vad händer om jag underkänns på det skarpa provet?",
    a: "Du får göra om provet. De flesta kommuner tar ut en avgift per försök (ofta 1 000–2 500 kr). Vi rekommenderar att du läser vår guide om underkända prov och kör flera provsimuleringar tills du stadigt klarar 80 %+.",
  },
];

export const Route = createFileRoute("/ovningsprov-serveringstillstand")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Övningsprov serveringstillstånd 2026 – simulera kunskapsprovet" },
      { name: "description", content: "Gratis övningsprov inför kunskapsprovet för serveringstillstånd. Simulera alkohollagens prov, träna alla 16 områden och vänj dig vid tidspressen. Uppdaterat för 2026." },
      { property: "og:title", content: "Övningsprov serveringstillstånd – simulera kunskapsprovet" },
      { property: "og:description", content: "Simulera kunskapsprovet i alkohollagen. Gratis exempelprov, alla 16 områden, uppdaterat för 2026 års regler." },
      { property: "og:url", content: "https://alkoholprovet.se/ovningsprov-serveringstillstand" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/ovningsprov-serveringstillstand" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Övningsprov", url: "https://alkoholprovet.se/ovningsprov-serveringstillstand" },
      ]),
      articleJsonLd({
        url: "https://alkoholprovet.se/ovningsprov-serveringstillstand",
        headline: "Övningsprov serveringstillstånd – simulera kunskapsprovet 2026",
        description: "Gratis övningsprov inför kunskapsprovet i alkohollagen. Simulera provet med blandade frågor från alla 16 områden.",
        datePublished: "2026-01-15",
        dateModified: "2026-06-11",
      }),
    ],
  }),
});

const cityLinks = [
  { to: "/serveringstillstand-stockholm", label: "Stockholm" },
  { to: "/serveringstillstand-goteborg", label: "Göteborg" },
  { to: "/serveringstillstand-malmo", label: "Malmö" },
  { to: "/serveringstillstand-uppsala", label: "Uppsala" },
  { to: "/serveringstillstand-vasteras", label: "Västerås" },
  { to: "/serveringstillstand-orebro", label: "Örebro" },
  { to: "/serveringstillstand-linkoping", label: "Linköping" },
  { to: "/serveringstillstand-helsingborg", label: "Helsingborg" },
  { to: "/serveringstillstand-jonkoping", label: "Jönköping" },
  { to: "/serveringstillstand-norrkoping", label: "Norrköping" },
] as const;

const verksamhetLinks = [
  { to: "/serveringstillstand-restaurang", label: "Restaurang" },
  { to: "/serveringstillstand-bar", label: "Bar & nattklubb" },
  { to: "/serveringstillstand-cafe", label: "Café" },
  { to: "/serveringstillstand-catering", label: "Catering" },
  { to: "/serveringstillstand-food-truck", label: "Food truck" },
  { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
] as const;

const kunskapsbankLinks = [
  { to: "/kunskapsbank/plugga-kunskapsprovet", label: "Plugga inför kunskapsprovet (4-veckorsplan)" },
  { to: "/kunskapsbank/kunskapsprovet-exempelfragor", label: "Fem exempelfrågor med facit" },
  { to: "/kunskapsbank/hur-svart-ar-kunskapsprovet", label: "Hur svårt är kunskapsprovet?" },
  { to: "/kunskapsbank/hur-manga-ratt-kunskapsprovet", label: "Hur många rätt krävs?" },
  { to: "/kunskapsbank/vanliga-misstag-kunskapsprovet", label: "Vanliga misstag på provet" },
  { to: "/kunskapsbank/underkand-pa-kunskapsprovet", label: "Underkänd – vad gör jag nu?" },
  { to: "/kunskapsbank/boka-kunskapsprovet", label: "Boka kunskapsprovet" },
  { to: "/kunskapsbank/alkohollagen-2026-viktiga-regler", label: "Alkohollagen 2026 – viktiga regler" },
] as const;

function Page() {
  return (
    <article className="container-narrow py-12 sm:py-16">
      <Breadcrumbs
        items={[
          { label: "Hem", to: "/" },
          { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
          { label: "Övningsprov" },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex rounded-full bg-secondary px-3 py-1 text-xs">Uppdaterat för 2026</span>
        <span className="inline-flex rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
          Senast granskad <time dateTime={LAST_REVIEWED}>{LAST_REVIEWED}</time>
        </span>
      </div>

      <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-balance">
        Övningsprov för serveringstillstånd
      </h1>
      <p className="mt-4 text-lg text-muted-foreground text-balance">
        Simulera kommunens kunskapsprov i alkohollagen direkt i webbläsaren. Träna under tidspress, få svar och förklaringar, och se exakt vilka områden du behöver repetera innan det skarpa provet. Gratis att starta – inget konto krävs för att testa fem frågor nedan.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to={FREE_CTA.to} className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 shadow-elevated">
          {FREE_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to={PREMIUM_CTA.to} className="inline-flex items-center gap-2 rounded-xl border border-accent/40 bg-accent/10 px-5 py-3 text-sm font-medium text-accent hover:bg-accent/15">
          {PREMIUM_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* GRATIS EXEMPELPROV */}
      <section className="mt-12">
        <div className="flex items-center gap-2 text-sm text-accent">
          <Sparkles className="h-4 w-4" />
          <span>Gratis exempelprov</span>
        </div>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold">Fem frågor i samma format som kunskapsprovet</h2>
        <p className="mt-3 text-muted-foreground">
          Frågorna nedan är konstruerade utifrån paragrafer i alkohollagen (2010:1622) och följer samma struktur som kommunens skarpa prov – ett scenario, fyra svarsalternativ och en lagförklaring. Inget konto, ingen inloggning. Klicka på ett svar för att se rätt alternativ och en förklaring.
        </p>
        <div className="mt-6">
          <MiniTasteTest />
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Vill du köra ett längre provsimulerat test? Starta vår <Link to="/exam" className="text-accent hover:underline">fullständiga provsimulering</Link> eller läs vidare för att förstå exakt hur det riktiga provet är uppbyggt.
        </p>
        <FreeQuizOffer
          className="mt-8"
          source="ovningsprov-serveringstillstand"
          heading="Klar med exempelfrågorna? Så klarar du provet"
        />
      </section>

      {/* HUR KUNSKAPSPROVET FUNGERAR */}
      <section className="mt-14">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold">Så fungerar kommunens kunskapsprov</h2>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Kunskapsprovet är ett obligatoriskt moment när du ansöker om stadigvarande serveringstillstånd enligt 8 kap. 12 § alkohollagen. Provet säkerställer att du som tillståndshavare har tillräckliga kunskaper för att driva en serveringsverksamhet i enlighet med lagen. Det administreras av kommunens tillståndsenhet, men frågedatabasen och kunskapsmodulerna kommer från Folkhälsomyndigheten. Det betyder att svårighetsgraden och innehållet är nationellt – det är samma kunskapskrav i Stockholm, Göteborg och Kiruna.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-medium"><Clock className="h-4 w-4 text-accent" /> Tid och längd</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Du har normalt 90 minuter på dig. Antalet frågor beror på vilket tillstånd du söker: 28 frågor för stadigvarande servering till slutet sällskap, upp till 60 frågor för stadigvarande servering till allmänheten med fullständiga rättigheter.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-medium"><Target className="h-4 w-4 text-accent" /> Godkäntgränser</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Enligt FoHMFS 2014:7 krävs minst 75 % rätt totalt. Många kommuner kräver dessutom godkänt per delområde – du kan alltså klara helheten men ändå bli underkänd om du har för stora luckor i ett enskilt område som marknadsföring eller serveringsansvar.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-medium"><FileCheck2 className="h-4 w-4 text-accent" /> Format</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Provet är skriftligt och består av flervalsfrågor. Varje fråga har normalt fyra svarsalternativ med ett rätt svar. Du svarar i en provportal hos kommunen – ingen lagbok, inga anteckningar, inget internet.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-medium"><MapPin className="h-4 w-4 text-accent" /> Plats och avgift</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Du gör provet hos kommunens tillståndsenhet. Avgiften varierar mellan 1 000 och 2 500 kr och tas ut per försök. Tolk till andra språk får användas men måste bokas i förväg.
            </p>
          </div>
        </div>

        <h3 className="mt-8 font-display text-xl font-semibold">De 15 kunskapsområdena</h3>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Folkhälsomyndigheten har delat in provet i 16 områden. Vårt övningsprov speglar exakt samma uppdelning så att du tränar i rätt proportioner: alkoholpolitik och folkhälsa, alkohollagens uppbyggnad, tillsyn och sanktioner, serveringstillståndets innehåll, lokalkrav och 2026 års förändringar, mat och dryck, marknadsföring och prissättning, ordning och nykterhet, åldersgränser och legitimationskontroll, serveringsansvarig person, narkotika och drogtecken, brandsäkerhet, bokföring och kassaregister, hygien och egenkontroll samt särskilda regler för cateringtillstånd och tillfälliga tillstånd.
        </p>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          De områden där flest underkänner sig är historiskt sett <em>marknadsföring</em> (det är förbjudet att rikta annonser till barn och unga, vilket många glömmer), <em>serveringsansvarig person</em> (alla 8 kap. 18 §-krav måste sitta) och <em>ordning och nykterhet</em> (skyldigheten att vägra och avvisa märkbart berusade gäster enligt 3 kap. 8 §).
        </p>
      </section>

      {/* HUR DU ANVÄNDER ÖVNINGSPROVET */}
      <section className="mt-14">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold">Så använder du övningsprovet smart</h2>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          De som klarar provet på första försöket har nästan alltid kört flera fullskaliga provsimuleringar innan. Det är inte mängden frågor som avgör – det är hur du <em>arbetar</em> med dem. Här är arbetsflödet vi rekommenderar:
        </p>

        <ol className="mt-5 space-y-4 text-sm text-muted-foreground">
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent font-semibold">1</span>
            <div>
              <strong className="text-foreground">Diagnosprov utan tidspress.</strong> Börja med en provsimulering där du <em>läser förklaringarna</em> efter varje svar. Målet är att förstå – inte att hinna. Notera vilka områden där du svarar fel.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent font-semibold">2</span>
            <div>
              <strong className="text-foreground">Områdesträning.</strong> Gå in i <Link to="/practice" className="text-accent hover:underline">områdesläget</Link> och fokusera på dina tre svagaste områden. Träna tills du klarar 80 %+ på varje.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent font-semibold">3</span>
            <div>
              <strong className="text-foreground">Ny provsimulering med tidspress.</strong> Kör fullt prov: 60 frågor på 30 minuter. Det är medvetet snabbare än kommunens 90 min – klarar du tempot här klarar du tempot där och har marginal kvar.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent font-semibold">4</span>
            <div>
              <strong className="text-foreground">Repetera fel.</strong> Gå tillbaka till de frågor du svarade fel på och läs lagförklaringen två gånger. Skriv ner paragrafen på en lapp.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent font-semibold">5</span>
            <div>
              <strong className="text-foreground">Final-check.</strong> Dagen innan det skarpa provet – kör en sista fullängdssimulering (60 frågor / 30 min). Ligger du på 80 %+ totalt och 70 %+ i alla områden är du redo.
            </div>
          </li>
        </ol>
      </section>

      {/* VANLIGA MISSTAG */}
      <section className="mt-14">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold">Sju vanliga misstag i övningsprov</h2>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Det här ser vi gång på gång när vi analyserar data från tusentals provsimuleringar:
        </p>
        <ul className="mt-5 space-y-3 text-sm">
          {[
            "Att gissa när du är osäker i stället för att läsa förklaringen och förstå <em>varför</em> rätt svar är rätt.",
            "Att enbart träna område för område och aldrig under tidspress – då blir det skarpa provet en chock.",
            "Att hoppa över marknadsföring och bokföring för att de känns ”tråkiga” – det är ofta där människor underkänns.",
            "Att memorera frågor i stället för paragrafer. Frågorna varierar; lagen är konstant.",
            "Att inte räkna med 2026 års regelförändringar (slopat matkrav, slopat krav på eget kök).",
            "Att läsa länge utan att testa sig själv. Forskning visar att aktiv återgivning är 3–5× mer effektivt än passiv läsning.",
            "Att panika på frågor om narkotika eller ordningsstörningar. Här gäller det att kunna 3 kap. 8 § och 8 kap. 20 § alkohollagen utantill.",
          ].map((m, i) => (
            <li key={i} className="flex gap-3 rounded-xl border border-border bg-card p-4">
              <XCircle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
              <span className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: m }} />
            </li>
          ))}
        </ul>
      </section>

      {/* GRATIS VS PREMIUM */}
      <section className="mt-14">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold">Gratis övningsprov vs Premium</h2>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Du kommer långt på de gratis verktygen – fem exempelfrågor här, fem dagliga frågor i <Link to="/practice" className="text-accent hover:underline">övningsläget</Link>, och en grundläggande provsimulering. Premium är till för dig som vill bli klar på 1–2 veckor utan begränsningar.
        </p>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">Gratis</h3>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> 5 exempelfrågor på den här sidan</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> 5 nya frågor per dag i övningsläget</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> En kort provsimulering per dag</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> Lagförklaring efter varje fråga</li>
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-accent bg-gradient-to-br from-accent/5 via-card to-card p-6 relative">
            <span className="absolute -top-2 right-4 rounded-full bg-accent px-3 py-0.5 text-xs font-medium text-accent-foreground">Rekommenderat</span>
            <h3 className="font-display text-lg font-semibold">Premium</h3>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> Obegränsat antal frågor, 800 i databasen</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> Fullängdssimulering (60 frågor / 30 min) – samma snittempo som kommunens 90 min på 60 frågor</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> Modulträning (15 frågor / 10 min) för alla 16 områden</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> Statistik per område och felsvarslista</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> Garanterat uppdaterat för 2026 års regler</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> 199 kr för 10 dagar eller 349 kr för 30 dagar – engångsbetalning, inget abonnemang</li>
            </ul>
            <Link to={PREMIUM_CTA.to} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
              {PREMIUM_CTA.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-14 rounded-2xl bg-surface-deep text-primary-foreground p-8 sm:p-10 text-center">
        <h2 className="font-display text-2xl sm:text-3xl font-bold">Redo att klara kunskapsprovet?</h2>
        <p className="mt-2 text-primary-foreground/80">
          92 % av våra användare klarar provet på första försöket. Engångsbetalning, inget abonnemang, inga dolda avgifter.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link to={FREE_CTA.to} className="inline-flex items-center gap-2 rounded-xl bg-white text-primary px-6 py-3 text-sm font-medium hover:bg-white/90">
            {FREE_CTA.label} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to={PREMIUM_CTA.to} className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-6 py-3 text-sm font-medium hover:bg-white/10">
            {PREMIUM_CTA.label}
          </Link>
        </div>
      </section>

      {/* CITY LINKS */}
      <section className="mt-14">
        <h2 className="font-display text-2xl font-semibold">Övningsprov efter stad</h2>
        <p className="mt-3 text-muted-foreground">
          Kunskapsprovet är nationellt, men ansökan, väntetider och avgifter skiljer sig mellan kommunerna. Läs lokal information för din ort:
        </p>
        <ul className="mt-5 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
          {cityLinks.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="inline-flex items-center gap-2 text-sm text-accent hover:underline">
                <MapPin className="h-4 w-4" /> Serveringstillstånd {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* VERKSAMHET LINKS */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-semibold">Övningsprov efter verksamhet</h2>
        <p className="mt-3 text-muted-foreground">
          Olika typer av verksamheter har olika fokusområden i kunskapsprovet. En food truck-operatör behöver kunna tillfälliga tillstånd, en nattklubb måste behärska reglerna om ordningsvakter och utökade serveringstider.
        </p>
        <ul className="mt-5 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
          {verksamhetLinks.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="inline-flex items-center gap-2 text-sm text-accent hover:underline">
                <Building2 className="h-4 w-4" /> {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* KUNSKAPSBANK */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-semibold">Fördjupa dig i kunskapsbanken</h2>
        <p className="mt-3 text-muted-foreground">
          Fördjupande artiklar som hjälper dig att prestera bättre på övningsprovet och det skarpa provet:
        </p>
        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
          {kunskapsbankLinks.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="inline-flex items-center gap-2 text-sm text-accent hover:underline">
                <ArrowRight className="h-4 w-4" /> {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="mt-14">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold">Vanliga frågor om övningsprovet</h2>
        <div className="mt-5 space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="group rounded-xl border border-border bg-card p-5">
              <summary className="cursor-pointer list-none flex justify-between gap-4 font-medium">
                <span>{f.q}</span>
                <span className="text-accent transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* SOURCES */}
      <section className="mt-12 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-xl font-semibold">Officiella källor</h2>
        <p className="mt-2 text-xs text-muted-foreground">
          Senast granskad <time dateTime={LAST_REVIEWED}>{LAST_REVIEWED}</time>. Kontrollera alltid aktuell information hos respektive myndighet.
        </p>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {[SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN].map((s) => (
            <li key={s.href}>
              <a href={s.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-accent hover:underline">
                <ArrowRight className="h-4 w-4" /> {s.label}
              </a>
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-10">
        <Disclaimer />
      </div>
    </article>
  );
}
